import { useCallback, useEffect, useMemo, useState } from 'react';

import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  brainsEndpoints,
  campaignsEndpoints,
  collectionsEndpoints,
  desksEndpoints,
  rulesEndpoints,
} from '@/api/endpoints';
import { type Desks } from '@/hooks/useDesks';
import { type Brains } from '@/shared/types/brain';
import { type Collections } from '@/shared/types/collections';
import { type Rule } from '@/shared/types/rules';
import { getChannelKey } from '@/util/channelName';

import { type EntityResult, type IntegrationSearchItem } from '../types';
import { assembleCampaignsFromCache, makeFuse } from '../utils';
import {
  searchBrains,
  searchCampaigns,
  searchCollections,
  searchDesks,
  searchRules,
} from './entitySearchers';

const MIN_QUERY_LENGTH = 2;

/** Subscribes to TanStack Query cache updates matching a predicate, returning a version counter that increments on each match. */
const useCacheVersion = (
  queryClient: QueryClient,
  predicate: (queryKey: unknown[]) => boolean
): number => {
  const [version, setVersion] = useState(0);

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (
        event?.type === 'updated' &&
        event?.query.queryKey &&
        Array.isArray(event.query.queryKey) &&
        predicate(event.query.queryKey)
      ) {
        setVersion((v) => v + 1);
      }
    });
    return unsubscribe;
  }, [queryClient, predicate]);

  return version;
};

/** Fuzzy-searches brains, collections, desks, campaigns, and integrations from the TanStack Query cache. */
export const useEntitySearch = (query: string): EntityResult[] => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Subscribe to cache updates reactively (enabled: false = no API calls)
  const { data: brainsData } = useQuery<Brains>({
    queryKey: [brainsEndpoints.brains],
    queryFn: () => Promise.reject(new Error('Cache-only query')),
    enabled: false,
  });

  const { data: collectionsData } = useQuery<Collections>({
    queryKey: [collectionsEndpoints.collections],
    queryFn: () => Promise.reject(new Error('Cache-only query')),
    enabled: false,
  });

  const { data: desksData } = useQuery<Desks>({
    queryKey: [desksEndpoints.desks],
    queryFn: () => Promise.reject(new Error('Cache-only query')),
    enabled: false,
  });

  // Augment desks with searchable keywords for service desks
  const desksWithKeywords = useMemo(() => {
    if (!desksData?.desks) {
      return [];
    }

    return desksData.desks.map((desk) => ({
      ...desk,
      // Service desks get additional searchable keywords
      _keywords: desk.is_service_desk
        ? [t('main_navigation.chat'), t('common.conversations')].join(' ')
        : '',
    }));
  }, [desksData, t]);

  // Track cache updates for campaigns and rules (infinite/dynamic query keys)
  const isCampaignKey = useCallback(
    (key: unknown[]) => key[0] === campaignsEndpoints.campaigns,
    []
  );
  const isRulesKey = useCallback(
    (key: unknown[]) => typeof key[0] === 'string' && (key[0] as string).includes('/rules'),
    []
  );
  const campaignsCacheVersion = useCacheVersion(queryClient, isCampaignKey);
  const rulesCacheVersion = useCacheVersion(queryClient, isRulesKey);

  // Assemble all rules from all desk caches
  const allRules = useMemo(() => {
    if (!desksData?.desks) {
      return [];
    }

    const rules: Rule[] = [];
    const seen = new Set<string>();

    for (const desk of desksData.desks) {
      const cacheKey = [rulesEndpoints.rules(desk.desk_id)];
      const cachedRules = queryClient.getQueryData<{ rules: Rule[] }>(cacheKey);

      if (!cachedRules?.rules) {
        continue;
      }

      for (const rule of cachedRules.rules) {
        if (seen.has(rule.rule_id)) {
          continue;
        }
        seen.add(rule.rule_id);
        rules.push(rule);
      }
    }

    return rules;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desksData, queryClient, rulesCacheVersion]);

  // Memoize Fuse instances - created once per data change, reused across searches
  const brainsFuse = useMemo(() => makeFuse(brainsData?.brains, ['name']), [brainsData]);
  const collectionsFuse = useMemo(
    () => makeFuse(collectionsData?.collections, ['name']),
    [collectionsData]
  );
  const desksFuse = useMemo(
    () => makeFuse(desksWithKeywords, ['name', '_keywords']),
    [desksWithKeywords]
  );
  const campaignsFuse = useMemo(
    () => makeFuse(assembleCampaignsFromCache(queryClient), ['name']),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [campaignsCacheVersion, queryClient]
  );

  // Assemble integration search items (memoized, only rebuilds when desksData changes)
  const integrationSearchItems = useMemo(() => {
    if (!desksData?.desks) {
      return [];
    }
    const items: IntegrationSearchItem[] = [];
    const seen = new Set<string>();

    for (const desk of desksData.desks) {
      for (const integration of desk.integrations ?? []) {
        if (seen.has(integration.integration_id)) {
          continue;
        } // Dedup by ID not type
        seen.add(integration.integration_id);

        const label = t(getChannelKey(integration.type), { defaultValue: integration.type });
        items.push({ integration, desk, label, type: integration.type });
      }
    }
    return items;
  }, [desksData, t]); // t is stable, safe in deps

  const integrationFuse = useMemo(
    () => makeFuse(integrationSearchItems, ['label', 'type']),
    [integrationSearchItems]
  );

  const rulesFuse = useMemo(() => makeFuse(allRules, ['name']), [allRules]);

  return useMemo(() => {
    if (query.length < MIN_QUERY_LENGTH) {
      return [];
    }

    const results = [
      ...searchBrains(query, brainsFuse),
      ...searchCollections(query, collectionsFuse),
      ...searchCampaigns(query, campaignsFuse, t),
      ...searchDesks(query, desksFuse, integrationFuse, desksData, t),
      ...searchRules(query, rulesFuse, desksData),
    ];

    results.sort((a, b) => b.score - a.score);
    return results;
  }, [
    query,
    brainsFuse,
    collectionsFuse,
    desksFuse,
    campaignsFuse,
    integrationFuse,
    rulesFuse,
    desksData,
    t,
  ]);
};
